import { useEffect, useRef, useCallback, useState } from 'react';
import { useAuth } from './useAuth';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '@/config/env';

// Debug mode - only log in development
const DEBUG = import.meta.env.DEV;
const log = (...args: any[]) => DEBUG && console.log(...args);
const logError = (...args: any[]) => DEBUG && console.error(...args);

/**
 * WebSocket 알림 타입
 */
export interface WebSocketNotification {
  type: string;
  message: string;
  timestamp: string | number;
  data?: any;
  [key: string]: any;
}

/**
 * WebSocket 연결 상태
 */
export type WebSocketConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

/**
 * WebSocket Hook 옵션
 */
interface UseWebSocketOptions {
  /** 알림 수신 시 콜백 */
  onNotification?: (notification: WebSocketNotification) => void;
  /** 연결 상태 변경 시 콜백 */
  onConnectionChange?: (status: WebSocketConnectionStatus) => void;
  /** 자동 재연결 여부 (기본: true) */
  autoReconnect?: boolean;
  /** 재연결 딜레이 (밀리초, 기본: 3000) */
  reconnectDelay?: number;
  /** 토스트 알림 표시 여부 (기본: true) */
  showToast?: boolean;
  /** ping 간격 (밀리초, 기본: 30000) */
  pingInterval?: number;
}

/**
 * WebSocket 실시간 알림 Hook
 *
 * 서버에서 WebSocket을 통해 실시간으로 알림을 수신하여 처리합니다.
 * SSE보다 양방향 통신이 가능하고 더 효율적입니다.
 *
 * @example
 * ```tsx
 * const { connectionStatus, notifications } = useWebSocket({
 *   onNotification: (notification) => {
 *     console.log('New notification:', notification);
 *   }
 * });
 * ```
 */
export const useWebSocket = (options: UseWebSocketOptions = {}) => {
  const {
    onNotification,
    onConnectionChange,
    autoReconnect = true,
    reconnectDelay = 3000,
    showToast = true,
    pingInterval = 30000,
  } = options;

  const { user, isAuthenticated, userType, accessToken } = useAuth();
  const webSocketRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const [connectionStatus, setConnectionStatus] = useState<WebSocketConnectionStatus>('disconnected');
  const [notifications, setNotifications] = useState<WebSocketNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // 사용자 ID 가져오기
  const getUserId = useCallback(() => {
    if (!user) return null;
    if (userType === 'admin') {
      return (user as any)?.id;
    } else if (userType === 'brand') {
      return (user as any)?.brandId || (user as any)?.brand_id || (user as any)?.id;
    }
    return null;
  }, [user, userType]);

  // 연결 상태 업데이트
  const updateConnectionStatus = useCallback((status: WebSocketConnectionStatus) => {
    setConnectionStatus(status);
    onConnectionChange?.(status);
  }, [onConnectionChange]);

  // WebSocket 연결 생성
  const connect = useCallback(() => {
    const userId = getUserId();
    if (!isAuthenticated || !userId || !accessToken) {
      return;
    }

    // 기존 연결 닫기
    if (webSocketRef.current) {
      webSocketRef.current.close();
    }

    updateConnectionStatus('connecting');

    // WebSocket URL 생성 — API_BASE_URL의 protocol만 ws(s)로 치환하여 사용.
    const wsBaseUrl = API_BASE_URL.replace(/^http/, 'ws').replace(/\/$/, '');
    const wsUrl = `${wsBaseUrl}/ws/notifications?userType=${userType}&userId=${userId}&token=${encodeURIComponent(accessToken)}`;

    log('[WebSocket] Connecting to:', wsUrl.replace(/token=[^&]+/, 'token=***'));

    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      log('[WebSocket] Connection opened');
      updateConnectionStatus('connected');

      // Start ping interval to keep connection alive
      pingIntervalRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, pingInterval);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        log('[WebSocket] Received message:', data);

        if (data.type === 'connected') {
          // Connection confirmation message
          updateConnectionStatus('connected');
          return;
        }

        if (data.type === 'notification') {
          const notification = data.data || data;
          handleNotification(notification);
        } else if (data.type) {
          // Other event types (e.g., direct notification)
          handleNotification(data);
        }
      } catch (e) {
        // Handle plain text messages (like pong)
        if (event.data === 'pong') {
          log('[WebSocket] Pong received');
          return;
        }
        logError('[WebSocket] Failed to parse message:', e);
      }
    };

    ws.onerror = (error) => {
      logError('[WebSocket] Connection error:', error);
      updateConnectionStatus('error');
    };

    ws.onclose = (event) => {
      log('[WebSocket] Connection closed:', event.code, event.reason);
      updateConnectionStatus('disconnected');

      // Clear ping interval
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }

      // 자동 재연결
      if (autoReconnect && event.code !== 1000) {
        reconnectTimeoutRef.current = setTimeout(() => {
          log('[WebSocket] Attempting to reconnect...');
          connect();
        }, reconnectDelay);
      }
    };

    webSocketRef.current = ws;
  }, [getUserId, isAuthenticated, accessToken, userType, updateConnectionStatus, autoReconnect, reconnectDelay, pingInterval]);

  // 알림 처리
  const handleNotification = useCallback((notification: WebSocketNotification) => {
    // 알림 추가
    setNotifications(prev => [notification, ...prev].slice(0, 50)); // 최대 50개 유지
    setUnreadCount(prev => prev + 1);

    // 콜백 호출
    onNotification?.(notification);

    // 토스트 알림 표시
    if (showToast && notification.message) {
      const toastType = getToastType(notification.type);
      if (toastType === 'success') {
        toast.success(notification.message);
      } else if (toastType === 'error') {
        toast.error(notification.message);
      } else {
        toast(notification.message, { icon: getNotificationIcon(notification.type) });
      }
    }
  }, [onNotification, showToast]);

  // 연결 해제
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    if (webSocketRef.current) {
      webSocketRef.current.close(1000, 'User disconnected');
      webSocketRef.current = null;
    }

    updateConnectionStatus('disconnected');
    log('[WebSocket] Disconnected');
  }, [updateConnectionStatus]);

  // 알림 읽음 처리
  const markAsRead = useCallback((notificationIndex?: number) => {
    if (notificationIndex !== undefined) {
      // 특정 알림만 읽음 처리
      setNotifications(prev =>
        prev.map((n, i) => i === notificationIndex ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } else {
      // 전체 읽음 처리
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    }
  }, []);

  // 알림 삭제
  const clearNotifications = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  // 인증 상태에 따라 연결/연결 해제
  useEffect(() => {
    if (isAuthenticated && accessToken) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [isAuthenticated, accessToken, connect, disconnect]);

  return {
    connectionStatus,
    notifications,
    unreadCount,
    connect,
    disconnect,
    markAsRead,
    clearNotifications,
  };
};

/**
 * 알림 타입에 따른 토스트 타입 결정
 */
function getToastType(notificationType: string): 'success' | 'error' | 'default' {
  switch (notificationType) {
    case 'SAMPLE_REQUEST_STATUS_CHANGE':
    case 'FEEDBACK_STATUS_CHANGE':
    case 'SAMPLE_REQUEST_UPDATED':
      return 'success';
    case 'ERROR':
      return 'error';
    default:
      return 'default';
  }
}

/**
 * 알림 타입에 따른 아이콘 결정
 */
function getNotificationIcon(notificationType: string): string {
  switch (notificationType) {
    case 'NEW_SAMPLE_REQUEST':
      return '📋';
    case 'SAMPLE_REQUEST_STATUS_CHANGE':
      return '📝';
    case 'SAMPLE_REQUEST_UPDATED':
      return '✏️';
    case 'NEW_FEEDBACK':
      return '💬';
    case 'FEEDBACK_STATUS_CHANGE':
      return '💡';
    case 'NEW_FEEDBACK_REPLY':
      return '💌';
    case 'PROJECT_UPDATE':
      return '📁';
    default:
      return '🔔';
  }
}

export default useWebSocket;
